import React, { useState, useEffect } from 'react';
import Icon from './ui-stubs';
import { Button } from './ui-stubs';
import { Input } from './ui-stubs';
import { Select } from './ui-stubs';
import { format } from 'date-fns';

const TaskDrawer = ({ 
  isOpen, 
  onClose, 
  task, 
  onSave, 
  canEdit, 
  allTasks, 
  getRiskLevel, 
  calculateTaskAging, 
  isOverdue,
  canTransition,
  statusConfig,
  isTaskBlocked,
  getBlockReason
}) => {
  // Mode Management: 'view', 'edit', 'create'
  const [mode, setMode] = useState('view');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    successCriteria: '',
    status: 'To Do',
    priority: 'Medium',
    owner: null, // Single task owner (mandatory)
    contributors: [], // Multiple contributors (optional)
    dueDate: '',
    startDate: '',
    tags: [],
    taskType: 'Development',
    dependencies: [],
    progress: 0,
    projectId: 'proj1',
    checklist: [], // Checklist items
  });

  const [activeTab, setActiveTab] = useState('details');
  const [newTag, setNewTag] = useState('');
  const [newComment, setNewComment] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState([]);
  const [copiedTaskId, setCopiedTaskId] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [editingChecklistTitle, setEditingChecklistTitle] = useState('');
  
  // Mock time entries for Time & Work Log
  const [timeEntries] = useState([
    {
      id: 'te-1',
      userId: 'u1',
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      startTime: '2026-01-23T09:00:00',
      endTime: '2026-01-23T11:15:00',
      duration: 135, // minutes
      progressNote: 'Completed initial wireframes and gathered feedback from stakeholders. Identified 3 key design improvements needed.',
      statusAtTime: 'In Progress'
    },
    {
      id: 'te-2',
      userId: 'u2',
      userName: 'Michael Chen',
      userAvatar: 'MC',
      startTime: '2026-01-22T14:30:00',
      endTime: '2026-01-22T17:45:00',
      duration: 195, // minutes
      progressNote: 'Set up development environment and implemented base component structure. Ready for design integration.',
      statusAtTime: 'In Progress'
    },
    {
      id: 'te-3',
      userId: 'u1',
      userName: 'Sarah Johnson',
      userAvatar: 'SJ',
      startTime: '2026-01-22T10:00:00',
      endTime: '2026-01-22T12:30:00',
      duration: 150, // minutes
      progressNote: 'Conducted user research and created initial design concepts. Documented findings in Figma.',
      statusAtTime: 'In Progress'
    },
    {
      id: 'te-4',
      userId: 'u3',
      userName: 'Emily Rodriguez',
      userAvatar: 'ER',
      startTime: '2026-01-21T15:00:00',
      endTime: '2026-01-21T16:20:00',
      duration: 80, // minutes
      progressNote: 'Reviewed requirements and provided HR compliance feedback on user flows.',
      statusAtTime: 'To Do'
    },
  ]);

  // Mock active tasking state (user currently clocked in)
  const [activeTasking] = useState({
    isActive: true,
    startTime: '2026-01-24T02:45:00',
    userId: 'u1',
    userName: 'Sarah Johnson',
    userAvatar: 'SJ'
  });

  // Calculate total time billed
  const totalTimeBilled = timeEntries?.reduce((sum, entry) => sum + entry?.duration, 0);
  
  // Calculate time billed by current user (assuming current user is u1)
  const currentUserId = 'u1';
  const currentUserTimeBilled = timeEntries?.filter(entry => entry?.userId === currentUserId)?.reduce((sum, entry) => sum + entry?.duration, 0);

  // Mock estimated time (in minutes)
  const estimatedTime = 600; // 10 hours

  // Format duration from minutes to readable format
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Calculate live duration for active tasking
  const [liveDuration, setLiveDuration] = useState(0);
  
  useEffect(() => {
    if (activeTasking?.isActive) {
      const interval = setInterval(() => {
        const start = new Date(activeTasking.startTime);
        const now = new Date();
        const diffMinutes = Math.floor((now - start) / 1000 / 60);
        setLiveDuration(diffMinutes);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [activeTasking]);
  
  // Mock activity log with human/system/AI differentiation
  const [activityLog, setActivityLog] = useState([
    {
      id: 1,
      type: 'human',
      action: 'comment',
      author: 'Sarah Johnson',
      avatar: 'SJ',
      content: 'Updated the design mockups based on feedback',
      timestamp: '2026-01-23T10:30:00',
    },
    {
      id: 2,
      type: 'system',
      action: 'status_change',
      author: 'System',
      avatar: 'SYS',
      content: 'Status changed from "To Do" to "In Progress"',
      metadata: { from: 'To Do', to: 'In Progress' },
      timestamp: '2026-01-23T09:15:00',
    },
    {
      id: 3,
      type: 'ai',
      action: 'suggestion',
      author: 'AI Assistant',
      avatar: 'AI',
      content: 'Suggested breaking this task into 3 subtasks for better tracking',
      timestamp: '2026-01-22T16:45:00',
    },
    {
      id: 4,
      type: 'human',
      action: 'assignment',
      author: 'Michael Chen',
      avatar: 'MC',
      content: 'Assigned to Sarah Johnson',
      timestamp: '2026-01-22T14:15:00',
    },
    {
      id: 5,
      type: 'system',
      action: 'priority_change',
      author: 'System',
      avatar: 'SYS',
      content: 'Priority changed from "Medium" to "High"',
      metadata: { from: 'Medium', to: 'High' },
      timestamp: '2026-01-22T11:30:00',
    },
  ]);

  // Mock AI suggestions
  const aiSuggestions = [
    {
      id: 'ai-1',
      type: 'next_action',
      title: 'Suggested next action',
      content: 'Schedule a design review meeting with stakeholders',
      icon: 'Lightbulb',
      color: 'blue'
    },
    {
      id: 'ai-2',
      type: 'automation',
      title: 'Automation opportunity',
      content: 'This task can be automated using workflow templates',
      icon: 'Zap',
      color: 'purple'
    },
    {
      id: 'ai-3',
      type: 'risk',
      title: 'Risk mitigation hint',
      content: 'Consider adding buffer time due to dependency on TASK-002',
      icon: 'Shield',
      color: 'amber'
    },
  ];

  // Add this block - Move assigneeOptions, priorityOptions, and taskTypeOptions here before they're used
  const assigneeOptions = [
    { value: 'u1', label: 'Sarah Johnson', avatar: 'SJ', role: 'Designer' },
    { value: 'u2', label: 'Michael Chen', avatar: 'MC', role: 'Developer' },
    { value: 'u3', label: 'Emily Rodriguez', avatar: 'ER', role: 'HR Manager' },
    { value: 'u4', label: 'David Kim', avatar: 'DK', role: 'DevOps' },
    { value: 'u5', label: 'Lisa Anderson', avatar: 'LA', role: 'Researcher' },
    { value: 'u6', label: 'Robert Taylor', avatar: 'RT', role: 'Designer' },
    { value: 'u7', label: 'Ahmed Al-Mansoori', avatar: 'AA', role: 'Planner' },
  ];

  const priorityOptions = [
    { value: 'Critical', label: 'Critical' },
    { value: 'High', label: 'High' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Low', label: 'Low' },
  ];

  const taskTypeOptions = [
    { value: 'Development', label: 'Development' },
    { value: 'Design', label: 'Design' },
    { value: 'Documentation', label: 'Documentation' },
    { value: 'Bug Fix', label: 'Bug Fix' },
    { value: 'Research', label: 'Research' },
    { value: 'Planning', label: 'Planning' },
    { value: 'Infrastructure', label: 'Infrastructure' },
  ];

  useEffect(() => {
    if (task) {
      setFormData({
        ...task,
        owner: task?.owner || null,
        contributors: task?.contributors || [],
        checklist: task?.checklist || [],
        successCriteria: task?.successCriteria || '',
      });
      setMode('view');
    } else {
      setFormData({
        title: '',
        description: '',
        successCriteria: '',
        status: 'To Do',
        priority: 'Medium',
        owner: null,
        contributors: [],
        dueDate: '',
        startDate: '',
        tags: [],
        taskType: 'Development',
        dependencies: [],
        progress: 0,
        projectId: 'proj1',
        checklist: [],
      });
      setMode('create');
    }
    setValidationErrors({});
    setActiveTab('details');
    setDismissedSuggestions([]);
  }, [task, isOpen]);

  const validateForm = () => {
    const errors = {};
    if (!formData?.title?.trim()) {
      errors.title = 'Title is required';
    }
    if (formData?.title?.length > 100) {
      errors.title = 'Title must be less than 100 characters';
    }
    if (!formData?.owner) {
      errors.owner = 'Task owner is required';
    }
    if (formData?.startDate && formData?.dueDate && formData?.startDate > formData?.dueDate) {
      errors.dueDate = 'Due date must be after start date';
    }
    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddTag = () => {
    if (newTag?.trim() && !formData?.tags?.includes(newTag?.trim())) {
      handleChange('tags', [...formData?.tags, newTag?.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleChange('tags', formData?.tags?.filter(tag => tag !== tagToRemove));
  };

  // Checklist handlers
  const handleAddChecklistItem = () => {
    if (newChecklistItem?.trim()) {
      const item = {
        id: `checklist-${Date.now()}`,
        title: newChecklistItem?.trim(),
        completed: false,
        assignee: null,
        dueDate: null,
        completedBy: null,
        completedAt: null,
      };
      handleChange('checklist', [...(formData?.checklist || []), item]);
      setNewChecklistItem('');
    }
  };

  const handleToggleChecklistItem = (itemId) => {
    const updatedChecklist = formData?.checklist?.map(item => {
      if (item?.id === itemId) {
        return {
          ...item,
          completed: !item?.completed,
          completedBy: !item?.completed ? 'Current User' : null,
          completedAt: !item?.completed ? new Date()?.toISOString() : null,
        };
      }
      return item;
    });
    handleChange('checklist', updatedChecklist);
  };

  const handleDeleteChecklistItem = (itemId) => {
    handleChange('checklist', formData?.checklist?.filter(item => item?.id !== itemId));
  };

  const handleStartEditChecklistItem = (item) => {
    setEditingChecklistId(item?.id);
    setEditingChecklistTitle(item?.title);
  };

  const handleSaveChecklistItemTitle = () => {
    if (editingChecklistTitle?.trim()) {
      const updatedChecklist = formData?.checklist?.map(item => 
        item?.id === editingChecklistId ? { ...item, title: editingChecklistTitle?.trim() } : item
      );
      handleChange('checklist', updatedChecklist);
    }
    setEditingChecklistId(null);
    setEditingChecklistTitle('');
  };

  const handleCancelEditChecklistItem = () => {
    setEditingChecklistId(null);
    setEditingChecklistTitle('');
  };

  const handleUpdateChecklistAssignee = (itemId, assigneeId) => {
    const updatedChecklist = formData?.checklist?.map(item => {
      if (item?.id === itemId) {
        const assignee = assigneeOptions?.find(a => a?.value === assigneeId);
        return { ...item, assignee: assignee ? { id: assigneeId, name: assignee?.label, avatar: assignee?.avatar } : null };
      }
      return item;
    });
    handleChange('checklist', updatedChecklist);
  };

  const handleUpdateChecklistDueDate = (itemId, dueDate) => {
    const updatedChecklist = formData?.checklist?.map(item => 
      item?.id === itemId ? { ...item, dueDate } : item
    );
    handleChange('checklist', updatedChecklist);
  };

  const getChecklistProgress = () => {
    if (!formData?.checklist || formData?.checklist?.length === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = formData?.checklist?.filter(item => item?.completed)?.length;
    const total = formData?.checklist?.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  };

  const handleAddComment = () => {
    if (newComment?.trim()) {
      const comment = {
        id: activityLog?.length + 1,
        type: 'human',
        action: 'comment',
        author: 'Current User',
        avatar: 'CU',
        content: newComment,
        timestamp: new Date()?.toISOString(),
      };
      setActivityLog(prev => [comment, ...prev]);
      setNewComment('');
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const taskData = {
      ...formData,
      id: task?.id || `TASK-${Date.now()}`,
      lastUpdated: new Date()?.toISOString()?.split('T')?.[0],
      createdDate: task?.createdDate || new Date()?.toISOString()?.split('T')?.[0],
    };
    onSave(taskData);
    onClose();
  };

  const handleCopyTaskId = () => {
    if (task?.id) {
      navigator.clipboard?.writeText(task?.id);
      setCopiedTaskId(true);
      setTimeout(() => setCopiedTaskId(false), 2000);
    }
  };

  const handleDismissSuggestion = (suggestionId) => {
    setDismissedSuggestions(prev => [...prev, suggestionId]);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
    } catch {
      return timestamp;
    }
  };

  const getDependencyTasks = () => {
    return formData?.dependencies?.map(depId => allTasks?.find(t => t?.id === depId))?.filter(Boolean) || [];
  };

  const getImpactInfo = () => {
    const project = { id: 'proj1', name: 'HR System Modernization', status: 'Active' };
    const goal = { id: 'goal1', name: 'Improve Employee Experience', progress: 65 };
    const risk = task ? getRiskLevel(task) : 'low';
    const riskExplanation = {
      critical: 'Overdue critical priority task',
      high: 'Blocked for more than 7 days or overdue',
      medium: 'Task aging over 30 days',
      low: 'Task is on track'
    };
    return { project, goal, risk, riskExplanation: riskExplanation?.[risk] };
  };

  const getDependencyHealth = () => {
    const deps = getDependencyTasks();
    const resolved = deps?.filter(d => d?.status === 'Done')?.length || 0;
    const blocking = deps?.filter(d => d?.status !== 'Done')?.length || 0;
    const atRisk = deps?.filter(d => isOverdue && isOverdue(d))?.length || 0;
    return { resolved, blocking, atRisk, total: deps?.length };
  };

  // Get allowed status transitions
  const getAllowedTransitions = () => {
    if (!task || !statusConfig || !Array.isArray(statusConfig)) return [];
    const currentStatusConfig = statusConfig?.find(s => s?.id === formData?.status);
    return currentStatusConfig?.allowedTransitions || [];
  };

  // Check if status transition is blocked
  const getTransitionBlockReason = (toStatus) => {
    if (!task || !canTransition) return null;
    const result = canTransition(task, formData?.status, toStatus);
    return result?.allowed ? null : result?.reason;
  };

  const canEditTask = task ? canEdit(task) : true;
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const isEditable = isEditMode || isCreateMode;

  if (!isOpen) return null;

  const impactInfo = getImpactInfo();
  const dependencyTasks = getDependencyTasks();
  const dependencyHealth = getDependencyHealth();
  const aging = task ? calculateTaskAging(task) : 0;
  const overdueStatus = task ? isOverdue(task) : false;
  const blocked = task && isTaskBlocked ? isTaskBlocked(task) : false;
  const blockReason = blocked && getBlockReason ? getBlockReason(task) : null;
  const allowedTransitions = getAllowedTransitions();
  const visibleSuggestions = aiSuggestions?.filter(s => !dismissedSuggestions?.includes(s?.id));

  // Risk color mapping
  const riskColors = {
    critical: 'text-red-600 bg-red-50 border-red-200',
    high: 'text-orange-600 bg-orange-50 border-orange-200',
    medium: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    low: 'text-green-600 bg-green-50 border-green-200'
  };

  const riskIcons = {
    critical: 'AlertOctagon',
    high: 'AlertTriangle',
    medium: 'AlertCircle',
    low: 'CheckCircle'
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-3xl bg-card shadow-xl z-50 overflow-y-auto">
        {/* Enhanced Header with Health Indicators */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 z-10 shadow-sm">
          {/* Mode Indicator Banner */}
          {isEditMode && (
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md flex items-center space-x-2">
              <Icon name="Edit" size={16} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Edit Mode</span>
            </div>
          )}
          {isCreateMode && (
            <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-md flex items-center space-x-2">
              <Icon name="Plus" size={16} className="text-green-600" />
              <span className="text-sm font-medium text-green-900">Create New Task</span>
            </div>
          )}

          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Title - Inline editable in edit mode */}
              {isEditable ? (
                <div className="mb-2">
                  <Input
                    value={formData?.title}
                    onChange={(e) => handleChange('title', e?.target?.value)}
                    placeholder="Enter task title"
                    disabled={!canEditTask}
                    className="text-xl font-bold"
                  />
                  {validationErrors?.title && (
                    <p className="text-xs text-red-600 mt-1">{validationErrors?.title}</p>
                  )}
                </div>
              ) : (
                <h2 className="text-xl font-bold text-gray-900 mb-2">{formData?.title}</h2>
              )}

              {/* Task ID with Copy Action */}
              {task && (
                <div className="flex items-center space-x-3 mb-3">
                  <button
                    onClick={handleCopyTaskId}
                    className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-primary transition-colors"
                  >
                    <span>{task?.id}</span>
                    <Icon name={copiedTaskId ? "Check" : "Copy"} size={14} />
                  </button>
                  {copiedTaskId && (
                    <span className="text-xs text-green-600">Copied!</span>
                  )}
                </div>
              )}

              {/* Health Indicators Row */}
              <div className="flex items-center flex-wrap gap-2">
                {/* Status Pill with Transition Awareness */}
                <div className="flex items-center space-x-1">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    formData?.status === 'Done' ? 'bg-green-100 text-green-800' :
                    formData?.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                    formData?.status === 'In Review' ? 'bg-purple-100 text-purple-800' :
                    formData?.status === 'Blocked' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {formData?.status}
                  </span>
                  {allowedTransitions?.length > 0 && isViewMode && (
                    <span className="text-xs text-gray-500">→ {allowedTransitions?.length} transitions</span>
                  )}
                </div>

                {/* Risk Indicator */}
                {task && (
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded border text-xs font-medium ${
                    riskColors?.[impactInfo?.risk]
                  }`}>
                    <Icon name={riskIcons?.[impactInfo?.risk]} size={14} />
                    <span>{impactInfo?.risk?.toUpperCase()} RISK</span>
                  </div>
                )}

                {/* Blocked State */}
                {blocked && (
                  <div className="flex items-center space-x-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-xs font-medium text-red-700">
                    <Icon name="Lock" size={14} />
                    <span>BLOCKED</span>
                  </div>
                )}

                {/* Overdue Indicator */}
                {overdueStatus && (
                  <div className="flex items-center space-x-1 px-2 py-1 rounded bg-red-50 border border-red-200 text-xs font-medium text-red-700">
                    <Icon name="Clock" size={14} />
                    <span>OVERDUE</span>
                  </div>
                )}

                {/* Read-only Badge */}
                {!canEditTask && task && (
                  <div className="flex items-center space-x-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                    <Icon name="Lock" size={12} />
                    <span>Read-only</span>
                  </div>
                )}
              </div>

              {/* Task Metadata */}
              {task && (
                <div className="mt-2 flex items-center space-x-3 text-xs text-gray-600">
                  <span>Created {aging} days ago</span>
                  {blockReason && (
                    <>
                      <span>•</span>
                      <span className="text-red-600 font-medium">{blockReason}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Header Actions */}
            <div className="flex items-center space-x-2 ml-4">
              {task && canEditTask && isViewMode && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => setMode('edit')}
                >
                  Edit
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* AI Suggestions (Non-intrusive) */}
        {task && isViewMode && visibleSuggestions?.length > 0 && (
          <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-border">
            <div className="space-y-2">
              {visibleSuggestions?.map(suggestion => (
                <div
                  key={suggestion?.id}
                  className={`flex items-start space-x-3 p-3 bg-white rounded-lg border border-${
                    suggestion?.color
                  }-200 shadow-sm`}
                >
                  <div className={`p-2 rounded-lg bg-${suggestion?.color}-100`}>
                    <Icon name={suggestion?.icon} size={16} className={`text-${suggestion?.color}-600`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">{suggestion?.title}</h4>
                    <p className="text-xs text-gray-600 mt-1">{suggestion?.content}</p>
                  </div>
                  <button
                    onClick={() => handleDismissSuggestion(suggestion?.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border px-6 bg-muted">
          <div className="flex space-x-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('impact')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'impact' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Impact & Dependencies
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Activity & Decisions
            </button>
            <button
              onClick={() => setActiveTab('timelog')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'timelog' ? 'border-primary text-primary' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Time & Work Log
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-8">
              {/* SECTION: Task Description */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2">
                  Task Description
                </h3>
                
                {isEditable ? (
                  <textarea
                    value={formData?.description}
                    onChange={(e) => handleChange('description', e?.target?.value)}
                    placeholder="Enter task description"
                    rows={4}
                    disabled={!canEditTask}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-700">{formData?.description || 'No description provided'}</p>
                )}
              </div>

              {/* SECTION: Success Criteria */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2">
                  Success Criteria
                </h3>
                
                {isEditable ? (
                  <textarea
                    value={formData?.successCriteria}
                    onChange={(e) => handleChange('successCriteria', e?.target?.value)}
                    placeholder="Define what success looks like for this task"
                    rows={3}
                    disabled={!canEditTask}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                ) : (
                  <p className="text-sm text-gray-700">{formData?.successCriteria || 'No success criteria defined'}</p>
                )}
              </div>

              {/* SECTION: Execution */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2">
                  Execution
                </h3>

                {/* Intelligent Status Transition Selector */}
                {isEditable ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status Transition</label>
                    {task && allowedTransitions?.length > 0 ? (
                      <div className="space-y-2">
                        {Array.isArray(statusConfig) && statusConfig?.map(status => {
                          const isAllowed = allowedTransitions?.includes(status?.id);
                          const isCurrent = formData?.status === status?.id;
                          const blockReason = getTransitionBlockReason(status?.id);
                          
                          if (isCurrent) return null;

                          return (
                            <button
                              key={status?.id}
                              onClick={() => isAllowed && !blockReason && handleChange('status', status?.id)}
                              disabled={!isAllowed || !!blockReason || !canEditTask}
                              className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                                isAllowed && !blockReason
                                  ? 'border-blue-200 bg-blue-50 hover:bg-blue-100 cursor-pointer' :'border-border bg-muted opacity-50 cursor-not-allowed'
                              }`}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-3 h-3 rounded-full ${status?.theme}`} />
                                <span className="text-sm font-medium text-gray-900">{status?.label}</span>
                              </div>
                              {blockReason && (
                                <span className="text-xs text-red-600 max-w-xs text-right">{blockReason}</span>
                              )}
                              {!isAllowed && !blockReason && (
                                <span className="text-xs text-gray-500">Not allowed from {formData?.status}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <Select
                        value={formData?.status}
                        onChange={(value) => handleChange('status', value)}
                        options={Array.isArray(statusConfig) ? statusConfig?.map(s => ({ value: s?.id, label: s?.label })) : []}
                        disabled={!canEditTask}
                      />
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
                        formData?.status === 'Done' ? 'bg-green-100 text-green-800' :
                        formData?.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        formData?.status === 'In Review' ? 'bg-purple-100 text-purple-800' :
                        formData?.status === 'Blocked' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {formData?.status}
                      </span>
                      {allowedTransitions?.length > 0 && (
                        <span className="text-xs text-gray-500">
                          Can transition to: {allowedTransitions?.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Priority */}
                <div className="grid grid-cols-2 gap-4">
                  {isEditable ? (
                    <Select
                      label="Priority"
                      value={formData?.priority}
                      onChange={(value) => handleChange('priority', value)}
                      options={priorityOptions}
                      disabled={!canEditTask}
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
                        formData?.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                        formData?.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                        formData?.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {formData?.priority}
                      </span>
                    </div>
                  )}

                  {/* Progress */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Progress</label>
                    {isEditable ? (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData?.progress}
                        onChange={(e) => handleChange('progress', parseInt(e?.target?.value) || 0)}
                        disabled={!canEditTask}
                      />
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Completion</span>
                          <span className="font-medium">{formData?.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${formData?.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* SECTION: Task Owner (Single, Mandatory) */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 flex items-center space-x-2">
                  <Icon name="User" size={16} />
                  <span>Task Owner</span>
                  <span className="text-xs font-normal text-red-600">(Required)</span>
                </h3>

                {formData?.owner ? (
                  <div className="flex items-center space-x-4 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <div className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center text-lg font-bold shadow-md">
                      {formData?.owner?.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="text-base font-bold text-gray-900">{formData?.owner?.name}</p>
                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded">OWNER</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{formData?.owner?.role || 'Accountable for task outcome'}</p>
                    </div>
                    {isEditable && canEditTask && (
                      <button
                        onClick={() => handleChange('owner', null)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-semibold flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} />
                      <span>No owner assigned</span>
                    </p>
                    <p className="text-xs text-red-700 mt-1">Every task must have a single accountable owner</p>
                  </div>
                )}

                {isEditable && canEditTask && !formData?.owner && (
                  <div>
                    <Select
                      placeholder="Select task owner"
                      value=""
                      onChange={(value) => {
                        const owner = assigneeOptions?.find(a => a?.value === value);
                        if (owner) {
                          handleChange('owner', { id: value, name: owner?.label, avatar: owner?.avatar, role: owner?.role });
                        }
                      }}
                      options={assigneeOptions}
                    />
                    {validationErrors?.owner && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors?.owner}</p>
                    )}
                  </div>
                )}
              </div>

              {/* SECTION: Contributors (Multiple, Optional) */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 flex items-center space-x-2">
                  <Icon name="Users" size={16} />
                  <span>Contributors</span>
                  <span className="text-xs font-normal text-gray-500">(Optional)</span>
                </h3>

                <p className="text-xs text-gray-600 italic">
                  Contributors collaborate on the task but are not accountable for the outcome
                </p>

                {formData?.contributors?.length > 0 ? (
                  <div className="space-y-2">
                    {formData?.contributors?.map(contributor => {
                      const contributorInfo = assigneeOptions?.find(a => a?.value === contributor?.id) || contributor;
                      return (
                        <div key={contributor?.id} className="flex items-center space-x-3 p-3 bg-muted border border-border rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="w-10 h-10 bg-gray-400 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                            {contributorInfo?.avatar || contributor?.avatar}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{contributor?.name}</p>
                            {contributorInfo?.role && (
                              <p className="text-xs text-gray-500">{contributorInfo?.role}</p>
                            )}
                          </div>
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-medium rounded">Contributor</span>
                          {isEditable && canEditTask && (
                            <button
                              onClick={() => handleChange('contributors', formData?.contributors?.filter(c => c?.id !== contributor?.id))}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Icon name="X" size={16} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-3 bg-muted border border-border rounded-lg text-center">
                    <p className="text-sm text-gray-500">No contributors added</p>
                  </div>
                )}

                {isEditable && canEditTask && (
                  <div className="mt-2">
                    <Select
                      placeholder="Add contributor"
                      value=""
                      onChange={(value) => {
                        const contributor = assigneeOptions?.find(a => a?.value === value);
                        if (contributor && !formData?.contributors?.some(c => c?.id === value) && formData?.owner?.id !== value) {
                          handleChange('contributors', [...formData?.contributors, { id: value, name: contributor?.label, avatar: contributor?.avatar, role: contributor?.role }]);
                        }
                      }}
                      options={assigneeOptions?.filter(opt => 
                        formData?.owner?.id !== opt?.value && 
                        !formData?.contributors?.some(c => c?.id === opt?.value)
                      )}
                    />
                  </div>
                )}
              </div>

              {/* SECTION: Checklist (Execution Breakdown) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center space-x-2">
                    <Icon name="CheckSquare" size={16} />
                    <span>Checklist</span>
                  </h3>
                  {formData?.checklist?.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-600">
                        {getChecklistProgress()?.completed} / {getChecklistProgress()?.total} completed
                      </span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${getChecklistProgress()?.percentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-600 italic">
                  Break down the task into actionable steps. Each item can have its own assignee and due date.
                </p>

                {/* Checklist Items */}
                {formData?.checklist?.length > 0 ? (
                  <div className="space-y-2">
                    {formData?.checklist?.map((item, index) => (
                      <div
                        key={item?.id}
                        className={`p-3 border rounded-lg transition-all ${
                          item?.completed ? 'bg-green-50 border-green-200' : 'bg-card border-border hover:border-foreground/20'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          {/* Checkbox */}
                          <button
                            onClick={() => handleToggleChecklistItem(item?.id)}
                            className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              item?.completed
                                ? 'bg-green-500 border-green-500' :'border-border hover:border-primary'
                            }`}
                            disabled={!canEditTask && !isViewMode}
                          >
                            {item?.completed && <Icon name="Check" size={14} className="text-white" />}
                          </button>

                          {/* Item Content */}
                          <div className="flex-1 space-y-2">
                            {/* Title */}
                            {editingChecklistId === item?.id ? (
                              <div className="flex items-center space-x-2">
                                <Input
                                  value={editingChecklistTitle}
                                  onChange={(e) => setEditingChecklistTitle(e?.target?.value)}
                                  onKeyPress={(e) => e?.key === 'Enter' && handleSaveChecklistItemTitle()}
                                  className="flex-1 text-sm"
                                  autoFocus
                                />
                                <button
                                  onClick={handleSaveChecklistItemTitle}
                                  className="text-green-600 hover:text-green-700 p-1"
                                >
                                  <Icon name="Check" size={16} />
                                </button>
                                <button
                                  onClick={handleCancelEditChecklistItem}
                                  className="text-gray-400 hover:text-gray-600 p-1"
                                >
                                  <Icon name="X" size={16} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  item?.completed ? 'text-gray-500 line-through' : 'text-gray-900'
                                }`}>
                                  {item?.title}
                                </p>
                                {canEditTask && (
                                  <div className="flex items-center space-x-1">
                                    <button
                                      onClick={() => handleStartEditChecklistItem(item)}
                                      className="text-gray-400 hover:text-primary p-1"
                                      title="Edit item"
                                    >
                                      <Icon name="Edit" size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteChecklistItem(item?.id)}
                                      className="text-gray-400 hover:text-red-600 p-1"
                                      title="Delete item"
                                    >
                                      <Icon name="Trash2" size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Assignee and Due Date */}
                            <div className="flex items-center space-x-4 text-xs">
                              {/* Assignee */}
                              <div className="flex items-center space-x-2">
                                {item?.assignee ? (
                                  <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                      {item?.assignee?.avatar}
                                    </div>
                                    <span className="text-gray-700">{item?.assignee?.name}</span>
                                    {canEditTask && (
                                      <button
                                        onClick={() => handleUpdateChecklistAssignee(item?.id, null)}
                                        className="text-gray-400 hover:text-red-600"
                                      >
                                        <Icon name="X" size={12} />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400">Unassigned</span>
                                )}
                                {canEditTask && !item?.assignee && (
                                  <select
                                    onChange={(e) => handleUpdateChecklistAssignee(item?.id, e?.target?.value)}
                                    className="text-xs border border-border rounded px-2 py-1"
                                    value=""
                                  >
                                    <option value="">Assign...</option>
                                    {assigneeOptions?.map(opt => (
                                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              {/* Due Date */}
                              <div className="flex items-center space-x-2">
                                {item?.dueDate ? (
                                  <div className="flex items-center space-x-2">
                                    <Icon name="Calendar" size={12} className="text-gray-500" />
                                    <span className="text-gray-700">{item?.dueDate}</span>
                                    {canEditTask && (
                                      <button
                                        onClick={() => handleUpdateChecklistDueDate(item?.id, null)}
                                        className="text-gray-400 hover:text-red-600"
                                      >
                                        <Icon name="X" size={12} />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  canEditTask && (
                                    <input
                                      type="date"
                                      onChange={(e) => handleUpdateChecklistDueDate(item?.id, e?.target?.value)}
                                      className="text-xs border border-border rounded px-2 py-1"
                                    />
                                  )
                                )}
                              </div>
                            </div>

                            {/* Completion Metadata */}
                            {item?.completed && item?.completedBy && (
                              <div className="flex items-center space-x-2 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                                <Icon name="CheckCircle" size={12} />
                                <span>
                                  Completed by {item?.completedBy} on {format(new Date(item?.completedAt), 'MMM dd, yyyy')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-muted border border-border rounded-lg text-center">
                    <Icon name="CheckSquare" size={32} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No checklist items yet</p>
                    <p className="text-xs text-gray-400 mt-1">Add items to break down this task into steps</p>
                  </div>
                )}

                {/* Add New Checklist Item */}
                {canEditTask && (
                  <div className="flex space-x-2">
                    <Input
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e?.target?.value)}
                      placeholder="Add a checklist item"
                      onKeyPress={(e) => e?.key === 'Enter' && handleAddChecklistItem()}
                      className="flex-1"
                    />
                    <Button size="sm" onClick={handleAddChecklistItem} iconName="Plus">
                      Add
                    </Button>
                  </div>
                )}
              </div>

              {/* SECTION: Timing */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2">
                  Timing
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {isEditable ? (
                    <>
                      <Input
                        label="Start Date"
                        type="date"
                        value={formData?.startDate}
                        onChange={(e) => handleChange('startDate', e?.target?.value)}
                        disabled={!canEditTask}
                      />
                      <div>
                        <Input
                          label="Due Date"
                          type="date"
                          value={formData?.dueDate}
                          onChange={(e) => handleChange('dueDate', e?.target?.value)}
                          disabled={!canEditTask}
                        />
                        {validationErrors?.dueDate && (
                          <p className="text-xs text-red-600 mt-1">{validationErrors?.dueDate}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <p className="text-sm text-gray-900">{formData?.startDate || 'Not set'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <p className="text-sm text-gray-900">{formData?.dueDate || 'Not set'}</p>
                        {overdueStatus && (
                          <p className="text-xs text-red-600 mt-1 font-medium">⚠️ Overdue</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Task Aging */}
                {task && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Task Age:</span>
                      <span className="font-medium text-gray-900">{aging} days</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Task Type */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b pb-2 mb-4">
                  Task Type
                </h3>
                {isEditable ? (
                  <Select
                    value={formData?.taskType}
                    onChange={(value) => handleChange('taskType', value)}
                    options={taskTypeOptions}
                    disabled={!canEditTask}
                  />
                ) : (
                  <p className="text-sm text-gray-900">{formData?.taskType}</p>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData?.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      <span>{tag}</span>
                      {isEditable && canEditTask && (
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-blue-900"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {isEditable && canEditTask && (
                  <div className="flex space-x-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e?.target?.value)}
                      placeholder="Add a tag"
                      onKeyPress={(e) => e?.key === 'Enter' && handleAddTag()}
                    />
                    <Button size="sm" onClick={handleAddTag} iconName="Plus">
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'impact' && (
            <div className="space-y-6">
              {/* Project → Goal → Task Chain */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Icon name="Target" size={18} />
                  <span>Strategic Context</span>
                </h3>
                
                {/* Chain Visualization */}
                <div className="space-y-3">
                  {/* Project */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Icon name="Briefcase" size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Project</p>
                      <p className="text-sm font-semibold text-gray-900">{impactInfo?.project?.name}</p>
                      <p className="text-xs text-gray-500">Status: {impactInfo?.project?.status}</p>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="ml-4 flex items-center text-gray-400">
                    <Icon name="ArrowDown" size={20} />
                  </div>

                  {/* Goal */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Icon name="Flag" size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">Goal</p>
                      <p className="text-sm font-semibold text-gray-900">{impactInfo?.goal?.name}</p>
                      <div className="mt-1">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Progress</span>
                          <span className="font-medium">{impactInfo?.goal?.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-purple-500 h-1.5 rounded-full"
                            style={{ width: `${impactInfo?.goal?.progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="ml-4 flex items-center text-gray-400">
                    <Icon name="ArrowDown" size={20} />
                  </div>

                  {/* This Task */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <Icon name="CheckSquare" size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-600">This Task</p>
                      <p className="text-sm font-semibold text-gray-900">{formData?.title}</p>
                      <p className="text-xs text-gray-500">{task?.id || 'New Task'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Explanation */}
              {task && (
                <div className={`border-2 rounded-lg p-4 ${riskColors?.[impactInfo?.risk]}`}>
                  <div className="flex items-start space-x-3">
                    <Icon name={riskIcons?.[impactInfo?.risk]} size={20} />
                    <div>
                      <h4 className="text-sm font-bold mb-1">{impactInfo?.risk?.toUpperCase()} Risk Level</h4>
                      <p className="text-sm">{impactInfo?.riskExplanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Dependency Health Summary */}
              <div className="bg-card border border-border rounded-lg p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Icon name="Link" size={18} />
                  <span>Dependency Health</span>
                </h3>

                {dependencyHealth?.total > 0 ? (
                  <>
                    {/* Health Metrics */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{dependencyHealth?.resolved}</p>
                        <p className="text-xs text-gray-600 mt-1">Resolved</p>
                      </div>
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{dependencyHealth?.blocking}</p>
                        <p className="text-xs text-gray-600 mt-1">Blocking</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{dependencyHealth?.atRisk}</p>
                        <p className="text-xs text-gray-600 mt-1">At Risk</p>
                      </div>
                    </div>

                    {/* Dependency List */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase">Dependencies ({dependencyTasks?.length})</h4>
                      {dependencyTasks?.map((depTask) => (
                        <div
                          key={depTask?.id}
                          className="flex items-center justify-between p-3 bg-muted border border-border rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{depTask?.title}</p>
                            <p className="text-xs text-gray-500">{depTask?.id}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              depTask?.status === 'Done' ? 'bg-green-100 text-green-800' : isOverdue && isOverdue(depTask) ?'bg-red-100 text-red-800': 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {depTask?.status}
                            </span>
                            {depTask?.status === 'Done' && (
                              <Icon name="CheckCircle" size={16} className="text-green-600" />
                            )}
                            {isOverdue && isOverdue(depTask) && (
                              <Icon name="AlertCircle" size={16} className="text-red-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Dependency Flow Visualization */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase mb-3">Dependency Flow</h4>
                      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
                        {dependencyTasks?.map((depTask, index) => (
                          <React.Fragment key={depTask?.id}>
                            <div className="flex-shrink-0 bg-card border-2 border-border rounded-lg px-3 py-2 text-xs min-w-[120px]">
                              <p className="font-semibold text-gray-900">{depTask?.id}</p>
                              <p className="text-gray-700 mt-1">{depTask?.status}</p>
                            </div>
                            <Icon name="ArrowRight" size={20} className="text-gray-400 flex-shrink-0" />
                          </React.Fragment>
                        ))}
                        <div className="flex-shrink-0 bg-primary/10 border-2 border-primary rounded-lg px-3 py-2 text-xs min-w-[120px]">
                          <p className="font-semibold text-primary">{task?.id || 'This Task'}</p>
                          <p className="text-gray-700 mt-1">{formData?.status}</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Link" size={48} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No dependencies</p>
                    <p className="text-xs text-gray-400 mt-1">This task can be completed independently</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-6">
              {/* Add Comment */}
              {isViewMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Comment
                  </label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e?.target?.value)}
                    placeholder="Write a comment..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm mb-2"
                  />
                  <Button size="sm" onClick={handleAddComment} iconName="Send">
                    Post Comment
                  </Button>
                </div>
              )}

              {/* Activity & Decisions Log */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Icon name="Activity" size={18} />
                  <span>Activity & Decisions</span>
                </h3>
                <div className="space-y-4">
                  {activityLog?.map((activity) => {
                    // Determine styling based on activity type
                    const typeStyles = {
                      human: { bg: 'bg-blue-500', border: 'border-blue-200', icon: 'User' },
                      system: { bg: 'bg-gray-500', border: 'border-border', icon: 'Settings' },
                      ai: { bg: 'bg-purple-500', border: 'border-purple-200', icon: 'Sparkles' },
                      timelog: { bg: 'bg-green-500', border: 'border-green-200', icon: 'Clock' }
                    };
                    const style = typeStyles?.[activity?.type] || typeStyles?.human;

                    return (
                      <div key={activity?.id} className={`flex space-x-3 p-3 rounded-lg border ${style?.border} bg-white`}>
                        <div className={`w-10 h-10 ${style?.bg} text-white rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0`}>
                          {activity?.type === 'human' ? activity?.avatar : <Icon name={style?.icon} size={16} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">{activity?.author}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              activity?.type === 'human' ? 'bg-blue-100 text-blue-700' :
                              activity?.type === 'system'? 'bg-gray-100 text-gray-700' : 
                              activity?.type === 'timelog' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {activity?.type?.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500">{formatTimestamp(activity?.timestamp)}</span>
                          </div>
                          <p className="text-sm text-gray-700">{activity?.content}</p>
                          
                          {/* Action-specific metadata */}
                          {activity?.metadata && (
                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                              {activity?.action === 'status_change' && (
                                <span>
                                  <span className="font-medium">{activity?.metadata?.from}</span>
                                  {' → '}
                                  <span className="font-medium">{activity?.metadata?.to}</span>
                                </span>
                              )}
                              {activity?.action === 'priority_change' && (
                                <span>
                                  <span className="font-medium">{activity?.metadata?.from}</span>
                                  {' → '}
                                  <span className="font-medium">{activity?.metadata?.to}</span>
                                </span>
                              )}
                              {activity?.action === 'timelog' && (
                                <div className="flex items-center space-x-2">
                                  <Icon name="Clock" size={14} />
                                  <span className="font-medium">{activity?.metadata?.duration}</span>
                                  <span>•</span>
                                  <span>{activity?.metadata?.progressNote}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timelog' && (
            <div className="space-y-6">
              {/* Active Tasking State Indicator */}
              {activeTasking?.isActive && (
                <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping opacity-75"></div>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-green-900">Currently Tasking In</p>
                        <p className="text-xs text-green-700">
                          {activeTasking?.userName} started at {format(new Date(activeTasking.startTime), 'h:mm a')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-900">{formatDuration(liveDuration)}</p>
                      <p className="text-xs text-green-700">Live Duration</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-white rounded border border-green-200">
                    <p className="text-xs text-gray-600">
                      <Icon name="Info" size={14} className="inline mr-1" />
                      <strong>How much effort has this task actually consumed?</strong> This summary helps you understand actual vs planned effort.
                    </p>
                  </div>
                </div>
              )}

              {/* Summary Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5">
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Icon name="BarChart3" size={18} />
                  <span>Time Billed Summary</span>
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  {/* Total Time Billed */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Clock" size={16} className="text-blue-600" />
                      <p className="text-xs text-gray-600 font-medium">Total Time Billed</p>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{formatDuration(totalTimeBilled)}</p>
                    <p className="text-xs text-gray-500 mt-1">{timeEntries?.length} time entries</p>
                  </div>

                  {/* Your Time Billed */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="User" size={16} className="text-indigo-600" />
                      <p className="text-xs text-gray-600 font-medium">Your Time Billed</p>
                    </div>
                    <p className="text-3xl font-bold text-indigo-900">{formatDuration(currentUserTimeBilled)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.round((currentUserTimeBilled / totalTimeBilled) * 100)}% of total
                    </p>
                  </div>

                  {/* vs Estimated */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon name="Target" size={16} className="text-purple-600" />
                      <p className="text-xs text-gray-600 font-medium">vs Estimated</p>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{formatDuration(estimatedTime)}</p>
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{Math.round((totalTimeBilled / estimatedTime) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            totalTimeBilled > estimatedTime ? 'bg-red-500' : 'bg-purple-500'
                          }`}
                          style={{ width: `${Math.min((totalTimeBilled / estimatedTime) * 100, 100)}%` }}
                        />
                      </div>
                      {totalTimeBilled > estimatedTime && (
                        <p className="text-xs text-red-600 mt-1 font-medium">
                          <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                          Over estimate by {formatDuration(totalTimeBilled - estimatedTime)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-xs text-blue-900">
                    <Icon name="Info" size={14} className="inline mr-1" />
                    <strong>How much effort has this task actually consumed?</strong> This summary helps you understand actual vs planned effort.
                  </p>
                </div>
              </div>

              {/* Work Log Timeline */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center space-x-2">
                  <Icon name="History" size={18} />
                  <span>Work Log Timeline</span>
                  <span className="text-xs font-normal text-gray-500">(Chronological Evidence)</span>
                </h3>

                <div className="space-y-4">
                  {timeEntries?.map((entry, index) => {
                    const isFirst = index === 0;
                    const isLast = index === timeEntries?.length - 1;
                    
                    return (
                      <div key={entry?.id} className="relative">
                        {/* Timeline connector */}
                        {!isLast && (
                          <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-gray-300"></div>
                        )}
                        <div className="flex space-x-4">
                          {/* User Avatar */}
                          <div className="relative flex-shrink-0">
                            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold border-4 border-white shadow-md z-10">
                              {entry?.userAvatar}
                            </div>
                            {isFirst && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white">
                                <Icon name="Star" size={10} className="text-white" />
                              </div>
                            )}
                          </div>

                          {/* Entry Content */}
                          <div className="flex-1 bg-card border border-border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="text-sm font-bold text-gray-900">{entry?.userName}</p>
                                <p className="text-xs text-gray-500">
                                  {format(new Date(entry.startTime), 'MMM d, yyyy • h:mm a')} → {format(new Date(entry.endTime), 'h:mm a')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xl font-bold text-blue-600">{formatDuration(entry?.duration)}</p>
                                <p className="text-xs text-gray-500">Duration</p>
                              </div>
                            </div>

                            {/* Progress Note (Primary Content) */}
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                              <div className="flex items-start space-x-2">
                                <Icon name="FileText" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-xs font-semibold text-blue-900 mb-1">Work Evidence</p>
                                  <p className="text-sm text-gray-800">{entry?.progressNote}</p>
                                </div>
                              </div>
                            </div>

                            {/* Status at Time */}
                            <div className="mt-3 flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Status during work:</span>
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                                  {entry?.statusAtTime}
                                </span>
                              </div>
                              
                              {/* Read-only indicator */}
                              <div className="flex items-center space-x-1 text-gray-400">
                                <Icon name="Lock" size={14} />
                                <span className="text-xs">Read-only</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {timeEntries?.length === 0 && (
                  <div className="text-center py-12 bg-muted rounded-lg border-2 border-dashed border-border">
                    <Icon name="Clock" size={48} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-600">No time entries logged yet</p>
                    <p className="text-xs text-gray-500 mt-1">Time entries will appear here when users clock in and out of this task</p>
                  </div>
                )}
              </div>

              {/* Editing & Integrity Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Shield" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-900 mb-2">Time Entry Integrity</h4>
                    <ul className="text-xs text-amber-800 space-y-1">
                      <li>• Time entries are <strong>read-only by default</strong> to maintain trust and accuracy</li>
                      <li>• Edits require special permissions and are <strong>visually marked as "Adjusted"</strong></li>
                      <li>• All modifications preserve original entry context for audit trails</li>
                      <li>• <strong>No silent edits</strong> — time tracking is a trust system</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Future-Ready Hooks */}
              <div className="bg-muted border border-border rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Sparkles" size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2">Future Capabilities (Coming Soon)</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-card p-3 rounded border border-border">
                        <Icon name="DollarSign" size={16} className="text-green-600 mb-1" />
                        <p className="text-xs font-medium text-gray-900">Cost Calculations</p>
                        <p className="text-xs text-gray-500 mt-1">Billable vs non-billable tracking</p>
                      </div>
                      <div className="bg-card p-3 rounded border border-border">
                        <Icon name="TrendingUp" size={16} className="text-blue-600 mb-1" />
                        <p className="text-xs font-medium text-gray-900">Performance Insights</p>
                        <p className="text-xs text-gray-500 mt-1">Effort vs outcome analysis</p>
                      </div>
                      <div className="bg-card p-3 rounded border border-border">
                        <Icon name="Brain" size={16} className="text-purple-600 mb-1" />
                        <p className="text-xs font-medium text-gray-900">AI Summaries</p>
                        <p className="text-xs text-gray-500 mt-1">Automated work log insights</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Context-Aware Footer Actions */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 shadow-lg">
          {isViewMode && task && (
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {allowedTransitions?.length > 0 && (
                  <span>Available transitions: <span className="font-medium">{allowedTransitions?.join(', ')}</span></span>
                )}
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                {canEditTask && (
                  <Button onClick={() => setMode('edit')} iconName="Edit">
                    Edit Task
                  </Button>
                )}
              </div>
            </div>
          )}

          {isEditMode && (
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => {
                  setMode('view');
                  setFormData(task);
                }}
              >
                Cancel
              </Button>
              <div className="flex space-x-3">
                <Button onClick={handleSave} disabled={!canEditTask}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {isCreateMode && (
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <div className="flex space-x-3">
                <Button onClick={handleSave} disabled={!canEditTask}>
                  Create Task
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TaskDrawer;